import { Component, OnInit } from '@angular/core';
import { get } from 'scriptjs';
import { Api, PAYMENT_SERVICE_V3 } from '../../../api/api';
import { ModalController } from '@ionic/angular';
import Payment from '../../../models/Payment';
import { environment } from '../../../environments/environment';
import Utils from '../../../utils';
import { PaymentMethod } from '../../../enums/PaymentMethod';
import Venue from '../../../models/Venue';
import { AnalyticsUtils } from '../../../utils/analytics-utils';
import Order from '../../../models/Order';
import { AngularFireAnalytics } from '@angular/fire/analytics';

export enum PaymentModalResult {
	SUCCESS,
	ERROR,
	DISMISS,
}

// tslint:disable-next-line:prefer-const
declare var wpwlOptions: any;

// @ts-ignore
const applePayAvailable = !!window.ApplePaySession;
@Component({
	selector: 'app-payment-modal',
	templateUrl: './payment-modal.component.html',
	styleUrls: ['payment-modal.component.scss'],
})
export class PaymentModalComponent implements OnInit {
	payment: Payment;
	venue: Venue;
	order: Order;
	loading = false;
	paymentMethods: PaymentMethod[] = [];
	selectedPaymentMethod: PaymentMethod = null;
	pm = PaymentMethod;
	vrUrl = environment.baseUrl + PAYMENT_SERVICE_V3 + 'vr/pay/';

	constructor(private modalCtrl: ModalController, private analytics: AngularFireAnalytics) {}

	static async show(
		modalCtrl: ModalController,
		venue: Venue,
		payment: Payment,
		order: Order
	): Promise<{ result: PaymentModalResult; error: any; payment: Payment }> {
		const paymentMethods: PaymentMethod[] = [];
		if (venue.ccEnabled) {
			paymentMethods.push(PaymentMethod.CREDIT_CARD);
		}
		if (venue.gpEnabled) {
			paymentMethods.push(PaymentMethod.GPAY);
		}
		if (venue.apEnabled && applePayAvailable) {
			paymentMethods.push(PaymentMethod.APAY);
		}
		if (venue.sbEnabled) {
			paymentMethods.push(PaymentMethod.SOFORTBANKING);
		}
		if (venue.ppEnabled) {
			paymentMethods.push(PaymentMethod.PAYPAL);
		}
		const modal = await modalCtrl.create({
			component: PaymentModalComponent,
			componentProps: {
				payment,
				paymentMethods,
				venue,
				order
			},
		});
		await modal.present();
		const result = await modal.onDidDismiss();
		await Utils.sleep(100);
		return result.data;
	}

	ngOnInit() {
		AnalyticsUtils.paymentModalOpened(this.analytics);
	}

	async loadPayment() {
		this.loading = true;
		this.payment._id = null;
		this.payment.method = this.selectedPaymentMethod;

		try {
			switch (this.selectedPaymentMethod) {
				case PaymentMethod.PAYPAL:
					await this.loadPaypal();
					break;
				case PaymentMethod.CREDIT_CARD:
					await this.loadCreditCard();
					break;
				case PaymentMethod.SOFORTBANKING:
					await this.loadKlarna();
					break;
				case PaymentMethod.GPAY:
					await this.loadGpay();
					break;
				case PaymentMethod.APAY:
					await this.loadApay();
					break;
			}
		} catch (e) {
			console.error(e);
		}
		this.loading = false;
		console.log(wpwlOptions);
		AnalyticsUtils.checkoutProgress(this.order, this.payment.method, 3, this.analytics);
	}

	async loadCreditCard(): Promise<any> {
		if (!this.venue.vrPaymentEnabled && this.venue.ppFullIntegrationEnabled) {
			return await this.loadPaypalCreditCard();
		}
		this.payment = (await Api.createPayment(this.payment, true));
		wpwlOptions = {
			locale: 'de',
			style: 'plain',
		};
		return new Promise<any>((resolve, reject) => {
			get(
				`${environment.VR_URL}?checkoutId=${this.payment.vrPayment.checkoutId}`,
				() => {
					resolve();
				},
				() => {
					reject('Could not load script');
				}
			);
		});
	}

	async loadGpay(): Promise<any> {
		this.payment = await Api.createPayment(this.payment, true);
		wpwlOptions = {
			locale: 'de',
			style: 'plain',
			googlePay: {
				gatewayMerchantId: this.payment.merchantId,
				allowedAuthMethods: ['PAN_ONLY'],
				allowedCardNetworks: ['MASTERCARD', 'VISA'],
				merchantName: this.venue.name,
				merchantId: environment.GPAY.MERCHANT_ID,
				gateway: environment.GPAY.GATEWAY
			},
		};
		return new Promise<any>((resolve, reject) => {
			get(
				`${environment.VR_URL}?checkoutId=${this.payment.vrPayment.checkoutId}`,
				() => {
					resolve();
				},
				() => {
					reject('Could not load script');
				}
			);
		});
	}

	async loadApay(): Promise<any> {
		this.payment = await Api.createPayment(this.payment, true);
		wpwlOptions = {
			locale: 'de',
			style: 'plain',
			createCheckout: () => {
				return this.payment.vrPayment.checkoutId;
			},
			applePay: {
				version: 3,
				displayName: this.venue.name,
				total: this.payment.sum,
				currencyCode: this.payment.currency,
				checkAvailability: 'canMakePayments',
				merchantIdentifier: environment.APAY_MERCHANT_ID,
				style: 'black',
				merchantCapabilities: ['supports3DS'],
				// supportedNetworks: ['masterCard', 'visa', 'maestro'],
				supportedNetworks: ['visa'],
				shippingType: 'storePickup',
				onCancel: () => {
					console.log('onCancel');
				},
				onPaymentMethodSelected: paymentMethod => {
					return {
						newTotal: this.payment.sum,
						newLineItems: [],
					};
				},
				onShippingMethodSelected: shippingMethod => {
					return {
						newTotal: this.payment.sum,
						newLineItems: [],
					};
				},
				onPaymentAuthorized: payment => {
					console.log('onPaymentAuthorized payment: ' + JSON.stringify(payment));
				},
			},
		};
		return new Promise<any>((resolve, reject) => {
			get(
				`${environment.VR_URL}?checkoutId=${this.payment.vrPayment.checkoutId}`,
				() => {
					resolve();
				},
				() => {
					reject('Could not load script');
				}
			);
		});
	}

	loadPaypalCreditCard(): Promise<any> {
		return new Promise<any>(async (resolve, reject) => {
			this.payment.method = PaymentMethod.PAYPAL;
			this.payment = await Api.createPayment(this.payment, this.venue.ppV2Enabled);
			const head = document.getElementsByTagName('head')[0];
			const script = document.createElement('script');
			script.type = 'text/javascript';
			if (this.venue.ppV2Enabled) {
				script.src = environment.PAYPAL.JS_URL_2 + this.payment.currency + '&merchant-id=' + this.payment.merchantId;
			} else {
				script.src = environment.PAYPAL.JS_URL + this.payment.currency;
			}
			script.setAttribute('data-partner-attribution-id', environment.PAYPAL.BN_CODE);
			head.appendChild(script);
			script.onerror = () => {
				reject('Could not load script');
			};
			script.onload = () => {
				// @ts-ignore
				const button = paypal.Buttons({
					intent: 'capture',
					commit: true,
					vault: false,
					enableStandardCardFields: true,
					createOrder: (data, actions) => {
						return this.payment.paypal.orderId;
					},
					onApprove: data => {
						this.loading = true;
						Api.finishPaypalPayment(data.orderID, this.venue.ppV2Enabled)
							.then(async result => {
								AnalyticsUtils.purchase(this.order, result, this.analytics);
								await this.modalCtrl.dismiss({
									result: PaymentModalResult.SUCCESS,
									payment: result,
								});
							})
							.finally(() => (this.loading = false));
					},
					onError: err => {
						this.loading = true;
						this.modalCtrl.dismiss({
							result: PaymentModalResult.ERROR,
							error: err,
						});
					},
					// @ts-ignore
					fundingSource: paypal.FUNDING.CARD,
				});
				if (button.isEligible()) {
					button.render('#paypal-cc-button');
				}
				resolve();
			};
		});
	}

	loadPaypal(): Promise<any> {
		return new Promise<any>(async (resolve, reject) => {
			this.payment = await Api.createPayment(this.payment, this.venue.ppV2Enabled);
			const head = document.getElementsByTagName('head')[0];
			const script = document.createElement('script');
			script.type = 'text/javascript';
			if (this.venue.ppV2Enabled) {
				script.src = environment.PAYPAL.JS_URL_2 + this.payment.currency + '&merchant-id=' + this.payment.merchantId;
			} else {
				script.src = environment.PAYPAL.JS_URL + this.payment.currency;
			}
			script.setAttribute('data-partner-attribution-id', environment.PAYPAL.BN_CODE);
			head.appendChild(script);
			script.onerror = () => {
				reject('Could not load script');
			};
			script.onload = () => {
				// @ts-ignore
				const button = paypal.Buttons({
					intent: 'capture',
					commit: true,
					vault: false,
					enableStandardCardFields: true,
					createOrder: (data, actions) => {
						return this.payment.paypal.orderId;
					},
					onApprove: data => {
						this.loading = true;
						Api.finishPaypalPayment(data.orderID, this.venue.ppV2Enabled)
							.then(async result => {
								AnalyticsUtils.purchase(this.order, result, this.analytics);
								await this.modalCtrl.dismiss({
									result: PaymentModalResult.SUCCESS,
									payment: result,
								});
							})
							.finally(() => (this.loading = false));
					},
					onError: err => {
						this.loading = true;
						this.modalCtrl.dismiss({
							result: PaymentModalResult.ERROR,
							error: err,
						});
					},
					// @ts-ignore
					fundingSource: paypal.FUNDING.PAYPAL,
				});
				if (button.isEligible()) {
					button.render('#paypal-button');
				}
				resolve();
			};
		});
	}

	loadKlarna(): Promise<any> {
		return new Promise<any>(async (resolve, reject) => {
			this.payment.method = PaymentMethod.PAYPAL;
			this.payment = await Api.createPayment(this.payment, this.venue.ppV2Enabled);
			const head = document.getElementsByTagName('head')[0];
			const script = document.createElement('script');
			script.type = 'text/javascript';
			if (this.venue.ppV2Enabled) {
				script.src = environment.PAYPAL.JS_URL_2 + this.payment.currency + '&merchant-id=' + this.payment.merchantId;
			} else {
				script.src = environment.PAYPAL.JS_URL + this.payment.currency;
			}
			script.setAttribute('data-partner-attribution-id', environment.PAYPAL.BN_CODE);
			head.appendChild(script);
			script.onerror = () => {
				reject('Could not load script');
			};
			script.onload = () => {
				// @ts-ignore
				const button = paypal.Buttons({
					intent: 'capture',
					commit: true,
					vault: false,
					enableStandardCardFields: true,
					createOrder: (data, actions) => {
						return this.payment.paypal.orderId;
					},
					onApprove: data => {
						this.loading = true;
						Api.finishPaypalPayment(data.orderID, this.venue.ppV2Enabled)
							.then(async result => {
								AnalyticsUtils.purchase(this.order, result, this.analytics);
								await this.modalCtrl.dismiss({
									result: PaymentModalResult.SUCCESS,
									payment: result,
								});
							})
							.finally(() => (this.loading = false));
					},
					onError: err => {
						this.loading = true;
						this.modalCtrl.dismiss({
							result: PaymentModalResult.ERROR,
							error: err,
						});
					},
					// @ts-ignore
					fundingSource: paypal.FUNDING.SOFORT,
				});
				if (button.isEligible()) {
					button.render('#klarna-button');
				}
				resolve();
			};
		});
	}

	dismiss() {
		if (this.loading) {
			return;
		}
		this.modalCtrl.dismiss({
			result: PaymentModalResult.DISMISS,
		});
	}
}
